import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState, VFC } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Tab, TabList, TabPanel, TabsProps } from 'react-tabs';
const Tabs = dynamic<TabsProps>(
  import('react-tabs').then((mod) => mod.Tabs),
  { ssr: false }
); // disable ssr

import { useFavoriteUsers } from '@/apis/aniftyBackend/creations/userFavorite/getFavoriteUsers';
import fetchUserByID from '@/apis/aniftyBackend/users/fetchUserByID';
import { User, isCreator } from '@/apis/aniftyBackend/users/interface';
import useFetchUserLikedCreations from '@/apis/aniftyBackend/users/useFetchUserLikedCreations';
import DefaultSpinner from '@/components/DefaultSpinner';
import NoItemsAnimon from '@/containers/UserProfilePage/NoItemsAnimon';
import FavoriteUserItem from '@/containers/UserProfilePage/UserItemsSection/FavoriteUserItem';
import useHash from '@/hooks/useHash';
import useWalletAddress from '@/hooks/useWalletAddress';
import { captureException } from '@/utils/Sentry';
import ChevronRight from '@/icons/chevron-right.svg';

import CreatedUserItem from './CreatedUserItem';
import CreationItem from './CreationItem';
import styles from './UserItemsSection.module.scss';
import UsersDraftItem from './UsersDraftItem';
import UsersLimitedCollectionItem from './UsersLimitedCollectionItem';
import ItemFilter from './ItemFilter';
import { FilterOptions } from './interface';
import { useFetchUserCollectedCreations } from '@/apis/aniftyBackend/users/useFetchUserCollectedCreations';
import { useFetchUserCreatedCreations } from '@/apis/aniftyBackend/users/useFetchUserCreatedCreations';
import { useFetchUserDraftCreations } from '@/apis/aniftyBackend/users/useFetchUserDraftCreations';
type Props = {
  user: User;
};

const UserItemsSection: VFC<Props> = ({ user }: Props) => {
  const { t } = useTranslation();

  const [hasFilter, setHasFilter] = useState(true);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    availability: 'all',
    sort: 'newest',
  });

  const onFilterChange = (filterOptionChanges: Partial<FilterOptions>) => {
    setFilterOptions({
      ...filterOptions,
      ...filterOptionChanges,
    });
  };

  // TO REMOVE: this function should be removed after liked creations pagination is fixed
  const applyFilterOnCreations = (creations) => {
    let filteredCreations = [...creations];
    let _exhaustivenessCheck: never;
    switch (filterOptions.availability) {
      case 'all':
        break;
      case 'sold':
        filteredCreations = creations.filter(
          (creation) => creation.availableTokenAmount === 0
        );
        break;
      case 'available':
        // Unnecessary to implement since this function is deprecated
        break;
      default:
        _exhaustivenessCheck = filterOptions.availability;
    }
    switch (filterOptions.sort) {
      case 'newest':
        break;
      case 'oldest':
        filteredCreations.reverse();
        break;
      default:
        _exhaustivenessCheck = filterOptions.sort;
    }
    return filteredCreations;
  };

  const { currentAccountAddress, loading } = useWalletAddress();
  const isMyItem = user.userID === currentAccountAddress;

  const {
    data: createdCreationListPages,
    fetchNextPage: fetchNextCreatedCreationList,
    hasNextPage: hasNextCreatedCreationList,
  } = useFetchUserCreatedCreations({
    userID: user.userID,
    filterCondition: filterOptions,
  });

  const createdCreations = useMemo(
    () => createdCreationListPages?.pages.flatMap((v) => v.createdCreations),
    [createdCreationListPages]
  );

  const {
    data: draftCreationListPages,
    fetchNextPage: fetchNextDraftCreationList,
    hasNextPage: hasNextDraftCreationList,
  } = useFetchUserDraftCreations({
    userID: user.userID,
    filterCondition: filterOptions,
  });

  const draftCreations = useMemo(
    () => draftCreationListPages?.pages.flatMap((v) => v.draftCreations),
    [draftCreationListPages]
  );

  const {
    data: collectedCreationListPages,
    fetchNextPage: fetchNextCollectedCreationList,
    hasNextPage: hasNextCollectedCreationList,
  } = useFetchUserCollectedCreations({
    userID: user.userID,
    filterCondition: filterOptions,
  });

  const collectedCreations = useMemo(
    () =>
      collectedCreationListPages?.pages.flatMap((v) => v.collectedCreations),
    [collectedCreationListPages]
  );

  const {
    data: likedCreationListPages,
    fetchNextPage: fetchNextLikedCreationList,
    hasNextPage: hasNextLikedCreationList,
  } = useFetchUserLikedCreations(user.userID);

  const likedCreations = useMemo(
    () => likedCreationListPages?.pages.flatMap((v) => v.likedCreations),
    [likedCreationListPages]
  );

  const {
    data: favoriteUsersPages,
    fetchNextPage: fetchNextFavoriteUsers,
    hasNextPage: hasNextFavoriteUsers,
  } = useFavoriteUsers({ userID: user.userID });

  const favoriteUsers = useMemo(
    () => favoriteUsersPages?.pages.flatMap((v) => v.favoriteUsers),
    [favoriteUsersPages]
  );

  const hashMappings = useMemo<string[]>(() => {
    const hashes = [
      'collected',
      'limited-collection',
      'liked',
      'favorite-profiles',
    ];
    if (isCreator(user)) {
      hashes.unshift('created');
    }
    return hashes;
  }, [user]);

  const [tabIndex, setTabIndex] = useState(0);
  const [hash, setHash] = useHash();

  // Reflect initial tab specified in hash
  useEffect(() => {
    if (hash && hashMappings.indexOf(hash) !== -1) {
      setTabIndex(hashMappings.indexOf(hash));
    }
  }, []);

  // Reflect current tab index to hash
  useEffect(() => {
    if (tabIndex < hashMappings.length) {
      setHash(hashMappings[tabIndex]);
    }
  }, [tabIndex]);

  /* This is a somewhat hacky solution:
   * After infinite scroll finishes loading created tokens, it will forget to load the second page of draft tokens
   * (Note that first page is always loaded from the beginning)
   * This useEffect is used to remind infinite scroll to keep loading
   */
  useEffect(() => {
    if (!hasNextCreatedCreationList && hasNextDraftCreationList) {
      fetchNextDraftCreationList();
    }
  }, [!hasNextCreatedCreationList && hasNextDraftCreationList]);

  return (
    <div className={styles.userItemSections}>
      <Tabs
        selectedIndex={tabIndex}
        onSelect={(index) => setTabIndex(index)}
        selectedTabClassName={
          tabIndex === 2
            ? styles.tabSelectedLimitedCollection
            : styles.tabSelected
        }
      >
        <TabList className={styles.tabList}>
          {isCreator(user) && (
            <Tab
              className={styles.tabSelector}
              onClick={() => setHasFilter(true)}
            >
              {t('profilePage.created')}
            </Tab>
          )}
          <Tab
            className={styles.tabSelector}
            onClick={() => setHasFilter(true)}
          >
            {t('profilePage.collected')}
          </Tab>
          <Tab
            className={styles.tabSelectorLimitedCollection}
            onClick={() => setHasFilter(false)}
          >
            {t('profilePage.limitedCollection')}
          </Tab>
          <Tab
            className={styles.tabSelector}
            onClick={() => setHasFilter(true)}
          >
            {t('profilePage.liked')}
          </Tab>
          <Tab
            className={styles.tabSelector}
            onClick={() => setHasFilter(false)}
          >
            {t('profilePage.favoriteProfiles')}
          </Tab>
        </TabList>

        {hasFilter &&
          (filterIsOpen ? (
            <ItemFilter
              filterOptions={filterOptions}
              onFilterChange={onFilterChange}
              closeFilter={() => setFilterIsOpen(false)}
            />
          ) : (
            <button
              type="button"
              className={styles.sidebarOpenButton}
              onClick={() => setFilterIsOpen(true)}
            >
              <ChevronRight />
            </button>
          ))}

        {/* Created Tab */}
        {isCreator(user) && (
          <TabPanel className={styles.tabContent}>
            {!createdCreations || !draftCreations ? (
              <DefaultSpinner />
            ) : createdCreations.length === 0 && draftCreations.length === 0 ? (
              <NoItemsAnimon />
            ) : (
              <InfiniteScroll
                className={styles.scroll}
                dataLength={createdCreations.length + draftCreations.length}
                next={
                  hasNextCreatedCreationList
                    ? fetchNextCreatedCreationList
                    : fetchNextDraftCreationList
                }
                hasMore={hasNextCreatedCreationList || hasNextDraftCreationList}
                loader={<DefaultSpinner />}
                endMessage={
                  <div className={styles.listBottomMessage}>
                    {t('marketplacePage.loadedAllItems', {
                      count: createdCreations.length + draftCreations.length,
                    })}
                  </div>
                }
              >
                <div className={styles.tokenListContainer}>
                  {createdCreations.map((item) => (
                    <CreatedUserItem
                      key={`created_creation_${item.creationID}`}
                      item={item}
                      isMyItem={isMyItem}
                      userName={user.userName}
                    />
                  ))}
                  {!hasNextCreatedCreationList &&
                    draftCreations.map((item) => (
                      <UsersDraftItem
                        key={`draft_creation_${item.creationID}`}
                        user={user}
                        draftToken={item}
                      />
                    ))}
                </div>
              </InfiniteScroll>
            )}
          </TabPanel>
        )}

        {/* Collected Tab */}
        <TabPanel>
          {!collectedCreations ? (
            <DefaultSpinner />
          ) : collectedCreations.length === 0 ? (
            <NoItemsAnimon />
          ) : (
            <InfiniteScroll
              next={fetchNextCollectedCreationList}
              hasMore={hasNextCollectedCreationList}
              loader={<DefaultSpinner />}
              dataLength={collectedCreations.length}
              endMessage={
                <div className={styles.listBottomMessage}>
                  {t('marketplacePage.loadedAllItems', {
                    count: collectedCreations.length,
                  })}
                </div>
              }
            >
              <div className={styles.tokenListContainer}>
                {collectedCreations.map((collectedCreation) => {
                  return (
                    <CreationItem
                      key={collectedCreation.creationID}
                      item={collectedCreation}
                    />
                  );
                })}
              </div>
            </InfiniteScroll>
          )}
        </TabPanel>

        {/* Limited Collection Tab */}
        <TabPanel>
          {user.collectedLimitedCollections?.length ? (
            <div className={styles.tokenListContainer}>
              {user.collectedLimitedCollections.map((item, index) => (
                <UsersLimitedCollectionItem key={index} item={item} />
              ))}
            </div>
          ) : (
            <NoItemsAnimon />
          )}
        </TabPanel>

        {/* Liked Tab */}
        <TabPanel>
          {!likedCreations ? (
            <DefaultSpinner />
          ) : applyFilterOnCreations(likedCreations).length === 0 ? (
            <NoItemsAnimon />
          ) : (
            <InfiniteScroll
              next={fetchNextLikedCreationList}
              hasMore={hasNextLikedCreationList}
              loader={<DefaultSpinner />}
              dataLength={applyFilterOnCreations(likedCreations).length}
            >
              <div className={styles.tokenListContainer}>
                {applyFilterOnCreations(likedCreations).map((likedCreation) => (
                  <CreationItem
                    key={likedCreation.creationID}
                    item={likedCreation}
                  />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </TabPanel>

        {/* Favorite Users Tab */}
        <TabPanel>
          {!favoriteUsers ? (
            <DefaultSpinner />
          ) : favoriteUsers.length === 0 ? (
            <NoItemsAnimon />
          ) : (
            <InfiniteScroll
              next={fetchNextFavoriteUsers}
              hasMore={hasNextFavoriteUsers}
              loader={<DefaultSpinner />}
              dataLength={favoriteUsers.length}
            >
              <div className={styles.tokenListContainer}>
                {favoriteUsers.map((favoriteUser) => (
                  <FavoriteUserItem
                    key={favoriteUser.userID}
                    favoriteUser={favoriteUser}
                  />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default UserItemsSection;
